import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  signOut as firebaseSignOut,
  updateProfile,
  GoogleAuthProvider 
} from 'firebase/auth';
import { auth } from './firebase';
import { withErrorHandling, ERROR_TYPES, createError, logError } from '../utils/errorHandling';

// Helper function to generate display name
function generateDisplayName(email, googleDisplayName = null) {
  let displayName;
  
  if (googleDisplayName) {
    // Use Google display name if available
    displayName = googleDisplayName;
  } else {
    // Use email prefix (part before @) for email/password signup
    displayName = email.split('@')[0];
  }
  
  // Truncate to 20 characters max
  if (displayName.length > 20) {
    displayName = displayName.substring(0, 20);
  }
  
  return displayName;
}

// Sign up with email and password
export async function signUp(email, password, displayName = null) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!email || !password) {
        throw createError(
          'Email and password are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Generate display name
      const finalDisplayName = displayName || generateDisplayName(email);
      
      // Update user profile with display name
      await updateProfile(user, {
        displayName: finalDisplayName
      });
      
      console.log('✅ User signed up successfully:', finalDisplayName);
      return {
        success: true,
        user,
        message: `Welcome to CollabCanvas, ${finalDisplayName}!`
      };
    },
    'User Signup',
    { 
      errorType: ERROR_TYPES.AUTH,
      enableRetry: false, // Don't retry auth operations
      enableOfflineQueue: false
    }
  );
}

// Sign in with email and password
export async function signIn(email, password) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!email || !password) {
        throw createError(
          'Email and password are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ User signed in successfully:', user.displayName || user.email);
      return {
        success: true,
        user,
        message: `Welcome back, ${user.displayName || 'User'}!`
      };
    },
    'User Sign In',
    { 
      errorType: ERROR_TYPES.AUTH,
      enableRetry: false,
      enableOfflineQueue: false
    }
  );
}

// Sign in with Google
export async function signInWithGoogle() {
  return await withErrorHandling(
    async () => {
      const provider = new GoogleAuthProvider();
      
      // Optional: Add additional scopes if needed
      // provider.addScope('profile');
      // provider.addScope('email');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Google sign-in automatically includes displayName from Google profile
      // But we'll ensure it follows our display name rules
      if (user.displayName) {
        const truncatedName = user.displayName.length > 20 
          ? user.displayName.substring(0, 20) 
          : user.displayName;
        
        // Update profile if we need to truncate
        if (truncatedName !== user.displayName) {
          await updateProfile(user, {
            displayName: truncatedName
          });
        }
      } else {
        // Fallback: use email prefix if Google doesn't provide displayName
        const fallbackName = generateDisplayName(user.email);
        await updateProfile(user, {
          displayName: fallbackName
        });
      }
      
      console.log('✅ User signed in with Google:', user.displayName || user.email);
      return {
        success: true,
        user,
        message: `Welcome to CollabCanvas, ${user.displayName}!`
      };
    },
    'Google Sign In',
    { 
      errorType: ERROR_TYPES.AUTH,
      enableRetry: false,
      enableOfflineQueue: false
    }
  );
}

// Sign out
export async function signOut() {
  return await withErrorHandling(
    async () => {
      await firebaseSignOut(auth);
      console.log('✅ User signed out successfully');
      return {
        success: true,
        message: 'You have been signed out successfully'
      };
    },
    'User Sign Out',
    { 
      errorType: ERROR_TYPES.AUTH,
      enableRetry: true,
      enableOfflineQueue: false
    }
  );
}

// Export auth instance for direct use if needed
export { auth } from './firebase';
